import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, message } from 'antd';
import {
  DashboardOutlined, TeamOutlined, EditOutlined, EnvironmentOutlined,
  LogoutOutlined, UserOutlined, MenuOutlined, RocketOutlined, MailOutlined, SearchOutlined,
  FireOutlined, TrophyOutlined, DollarOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import NotificationCenter from '../../components/NotificationCenter';
import ContextSwitcher from '../../components/community/ContextSwitcher';
import AdBanner from '../../components/AdBanner';
import PushNotificationButton from '../../components/PushNotificationButton';
import ChatInboxWidget from '../../components/community/ChatInboxWidget';
import useIsMobile from '../../utils/viewport';
import api, { PLAYER_ACTIVE_TEAM_KEY } from '../../services/api';

const { Header, Content, Sider } = Layout;

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const SIDER_BG = '#0f1f1a';
const SIDE_GRID_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><path d='M40 0H0v40' fill='none' stroke='rgba(255,255,255,0.05)' stroke-width='1'/></svg>\")";

// Layout separado pra area do dono de time-comunidade.
// Menu tem itens do time (nao mistura com jogador). Switcher no header
// permite voltar pra modo Jogador com 1 clique.
// Auth check SÍNCRONO — roda antes do primeiro render pra evitar flash
// de painel + redirect lento com window.location.href.
function resolveOwnerAuth() {
  const token = localStorage.getItem('community_token');
  const userData = localStorage.getItem('community_user');
  if (!token || !userData) return { ok: false, user: null };
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    if (payload.exp < now || payload.type !== 'player') {
      localStorage.removeItem('community_token');
      localStorage.removeItem('community_user');
      return { ok: false, user: null };
    }
    return { ok: true, user: JSON.parse(userData) };
  } catch {
    localStorage.removeItem('community_token');
    localStorage.removeItem('community_user');
    return { ok: false, user: null };
  }
}

export default function TeamOwnerLayout({ children }) {
  const nav = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const [collapsed, setCollapsed] = useState(true);
  const [authState] = useState(() => resolveOwnerAuth());
  const user = authState.user;
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState(null);

  // Deriva o item ativo do path atual (reage a navegacoes SPA sem reload)
  const selectedKey = (() => {
    const p = location.pathname;
    if (p.includes('/dashboard')) return 'dashboard';
    if (p.includes('/feed')) return 'feed';
    if (p.includes('/mapa')) return 'mapa';
    if (p.includes('/procurar')) return 'procurar';
    if (p.includes('/convites')) return 'convites';
    if (p.includes('/editar')) return 'editar';
    if (p.includes('/upgrade')) return 'upgrade';
    if (p.includes('/setup')) return 'setup';
    if (p.includes('/financeiro/cobrancas') || p.includes('/financeiro/lote') || p.includes('/financeiro/nova-cobranca')) return 'fin-cobrancas';
    if (p.includes('/financeiro/recorrencias')) return 'fin-recorrencias';
    if (p.includes('/financeiro/contatos')) return 'fin-contatos';
    if (p.includes('/financeiro/extrato')) return 'fin-extrato';
    if (p.includes('/financeiro/configuracoes')) return 'fin-configuracoes';
    if (p.includes('/financeiro')) return 'fin-dashboard';
    return '';
  })();

  useEffect(() => { setCollapsed(isMobile); }, [isMobile]);

  // Auth ja resolvido sincronamente via authState — sem useEffect necessario.

  useEffect(() => {
    // Time ativo: helper resolve URL → localStorage → primeiro time.
    // Grava no localStorage pra manter escolha entre telas sem :id (jogos,
    // meus-amistosos, convites, feed).
    const { resolveActiveTeam } = require('../../utils/activeTeam');
    api.get('/community/team-owner/my').then(r => {
      const t = resolveActiveTeam(r.data?.teams || []);
      if (t) {
        setTeamId(t.id);
        setTeamName(t.name || '');
      }
    }).catch(() => {});
  }, []);

  // Financeiro ativado? Se sim, mostra submenu (Dashboard/Cobranças/
  // Contatos/Extrato/Configurações). Se não, item simples → Onboarding.
  const [financeActivated, setFinanceActivated] = useState(false);
  useEffect(() => {
    if (!teamId) return;
    api.get('/community/finance/access-check', { params: { team_id: teamId } })
      .then(r => setFinanceActivated(r.data?.activated === true))
      .catch(() => setFinanceActivated(false));
  }, [teamId]);

  // Menu openKeys reativo — mantém "Financeiro" aberto enquanto o user
  // navega em qualquer /financeiro/* pra ele ver qual sub-item está ativo.
  // User ainda pode fechar clicando no parent; se navegar pra outro fin-*,
  // reabre automaticamente.
  const [openKeys, setOpenKeys] = useState([]);
  useEffect(() => {
    if (selectedKey?.startsWith('fin-')) {
      setOpenKeys(prev => prev.includes('financeiro') ? prev : [...prev, 'financeiro']);
    }
  }, [selectedKey]);

  const handleLogout = () => {
    localStorage.removeItem('community_token');
    localStorage.removeItem('community_user');
    localStorage.removeItem(PLAYER_ACTIVE_TEAM_KEY);
    window.location.href = '/player/login';
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Painel do time',
      onClick: () => teamId ? nav(`/time/dashboard/${teamId}`) : message.info('Sem time cadastrado'),
    },
    {
      key: 'feed',
      icon: <FireOutlined />,
      label: 'Feed',
      onClick: () => nav('/time/feed'),
    },
    {
      key: 'mapa',
      icon: <EnvironmentOutlined />,
      label: 'Mapa',
      onClick: () => nav('/time/mapa'),
    },
    {
      key: 'procurar',
      icon: <SearchOutlined />,
      label: 'Procurar jogadores',
      onClick: () => nav('/time/procurar'),
    },
    {
      key: 'elenco',
      icon: <TeamOutlined />,
      label: 'Elenco',
      onClick: () => nav('/time/elenco'),
    },
    {
      key: 'escalacao',
      icon: <TeamOutlined />,
      label: 'Escalação',
      onClick: () => nav('/time/escalacao'),
    },
    {
      key: 'convites',
      icon: <MailOutlined />,
      label: 'Convites do time',
      onClick: () => nav('/time/convites'),
    },
    {
      key: 'amistosos',
      icon: <RocketOutlined />,
      label: 'Amistosos',
      onClick: () => nav('/time/amistosos'),
    },
    {
      key: 'meus-amistosos',
      icon: <RocketOutlined />,
      label: 'Meus amistosos',
      onClick: () => nav('/time/meus-amistosos'),
    },
    {
      key: 'jogos',
      icon: <TrophyOutlined />,
      label: 'Jogos',
      onClick: () => nav('/time/jogos'),
    },
    financeActivated ? {
      key: 'financeiro',
      icon: <DollarOutlined />,
      label: 'Financeiro',
      children: [
        { key: 'fin-dashboard', label: 'Dashboard', onClick: () => nav('/comunidade/financeiro') },
        { key: 'fin-cobrancas', label: 'Cobranças', onClick: () => nav('/comunidade/financeiro/cobrancas') },
        { key: 'fin-recorrencias', label: 'Recorrências', onClick: () => nav('/comunidade/financeiro/recorrencias') },
        { key: 'fin-contatos', label: 'Contatos', onClick: () => nav('/comunidade/financeiro/contatos') },
        { key: 'fin-extrato', label: 'Minha conta', onClick: () => nav('/comunidade/financeiro/extrato') },
        { key: 'fin-configuracoes', label: 'Configurações', onClick: () => nav('/comunidade/financeiro/configuracoes') },
      ],
    } : {
      key: 'financeiro',
      icon: <DollarOutlined />,
      label: 'Financeiro',
      onClick: () => nav('/comunidade/financeiro'),
    },
    {
      key: 'editar',
      icon: <EditOutlined />,
      label: 'Editar time',
      onClick: () => teamId ? nav(`/time/editar/${teamId}`) : message.info('Sem time cadastrado'),
    },
    {
      key: 'upgrade',
      icon: <RocketOutlined />,
      label: 'Virar Futezap Pro',
      onClick: () => teamId ? nav(`/time/upgrade/${teamId}`) : null,
    },
  ];

  const userMenu = {
    items: [
      { key: 'player', icon: <UserOutlined />, label: 'Ir pra Modo Jogador', onClick: () => window.location.href = '/player/dashboard' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Sair', danger: true, onClick: handleLogout },
    ],
  };

  const brandBadge = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '20px 20px 16px 20px', color: '#fff' }}>
      <img src="/pwa/icon-192.png" alt="Futezap" width={40} height={40}
        style={{ borderRadius: 12, boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }} />
      <div>
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.3 }}>Futezap</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5 }}>PAINEL DO TIME</div>
      </div>
    </div>
  );

  // Guard sincrono: sem auth valida, redireciona instantaneamente pro login.
  // <Navigate> nao remonta o app (diferente de window.location.href) — evita
  // flash do painel e o "delay de segundos" ate a tela de login aparecer.
  if (!authState.ok) {
    const next = encodeURIComponent(window.location.pathname);
    return <Navigate to={`/player/login?next=${next}`} replace />;
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {isMobile && !collapsed && (
        <div onClick={() => setCollapsed(true)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000 }} />
      )}

      <Sider
        collapsible={false}
        collapsed={isMobile ? collapsed : false}
        width={230}
        collapsedWidth={0}
        style={{
          position: isMobile ? 'fixed' : 'relative',
          zIndex: isMobile ? 10001 : 'auto',
          height: isMobile ? '100vh' : 'auto',
          left: isMobile ? (collapsed ? -240 : 0) : 0,
          transition: 'left 0.3s ease',
          background: SIDER_BG,
          backgroundImage: SIDE_GRID_BG,
          boxShadow: isMobile && !collapsed ? '2px 0 20px rgba(0,0,0,0.35)' : 'none',
        }}
        // AntD Sider tem um filho interno `.ant-layout-sider-children` com
        // estilos próprios que interferem no flex. Passamos className pra
        // usar CSS que sobrescreve via seletor filho direto.
        className="team-owner-sider"
      >
        {/* Header fixo (brand + switcher mobile) — não scroll */}
        <div style={{ flexShrink: 0 }}>
          {brandBadge}
          {isMobile && (
            <div style={{ padding: '0 16px 12px 16px' }}>
              <ContextSwitcher mode="team-owner" />
            </div>
          )}
        </div>
        {/* Menu scrollável ocupa espaço restante — permite navegar todos os
            itens em mobile mesmo com submenu financeiro expandido. */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            items={menuItems.map(i => ({ ...i, style: { fontWeight: 500 } }))}
            style={{ background: 'transparent', borderRight: 'none', fontSize: 14 }}
          />
        </div>
        {/* Footer fixo no bottom — não sobrepõe mais os itens do menu */}
        <div style={{
          flexShrink: 0,
          margin: 20,
          padding: 12, borderRadius: 12,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.65)', fontSize: 11,
        }}>
          Modo dono do time.<br />1 conta, 2 chapéus.
        </div>
        {/* CSS pra forçar o filho interno .ant-layout-sider-children a ser
            flex column full-height, permitindo o wrapper scroll funcionar. */}
        <style>{`
          .team-owner-sider .ant-layout-sider-children {
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
          }
        `}</style>
      </Sider>

      <Layout style={{ background: 'transparent' }}>
        <Header style={{
          padding: isMobile ? '0 8px' : '0 24px',
          background: '#fff',
          borderBottom: '1px solid #eef1f4',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 64,
          overflowX: 'hidden', minWidth: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
            {isMobile && (
              <Button type="text" icon={<MenuOutlined style={{ fontSize: 18 }} />}
                onClick={() => setCollapsed(!collapsed)} />
            )}
            {!isMobile && <ContextSwitcher mode="team-owner" />}
          </div>

          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 12, flexShrink: 0 }}>
              <PushNotificationButton
                compact={isMobile}
                basePath="/push/player"
                contextText="Este dispositivo recebe notificações do teu time-comunidade."
              />
              <ChatInboxWidget />
              <NotificationCenter userType="player" />
              <Dropdown menu={userMenu} placement={isMobile ? 'bottomLeft' : 'bottomRight'} trigger={['click']}>
                <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', height: 'auto' }}>
                  <Avatar
                    size={36}
                    src={user.profile_photo}
                    icon={<TeamOutlined />}
                    style={{
                      background: user.profile_photo ? 'transparent' : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                    }}
                  />
                  {!isMobile && (
                    <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>{user.name?.split(' ')[0] || 'Dono'}</div>
                      <div style={{ fontSize: 11, color: '#128C7E' }}>{teamName || 'Dono do time'}</div>
                    </div>
                  )}
                </Button>
              </Dropdown>
            </div>
          )}
        </Header>

        <Content style={{ background: 'transparent', minHeight: 'calc(100vh - 64px)' }}>
          <AdBanner communityMode />
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
